import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, Typography,TextField, Button,  
    CircularProgress, FormControlLabel, Checkbox, MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const JobGradePage = () => {
    useAuthCheck();
    const { jobGradeId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formValues, setFormValues] = useState({
        jobGradeCode: '',
        description: '',
        jobGradeType: '',
        jobGradePriority: 0,
        isActive: true,
    });


    useEffect(() => {     
        const fetchJobGrade = async () => {
            try {
                setLoading(true);

                if (jobGradeId) {
                    const res = await api.get(`/jobgrades/${jobGradeId}`);

                    setFormValues({
                        jobGradeCode: res.data.jobGradeCode || '',
                        description: res.data.description || '',
                        jobGradeType: res.data.jobGradeType || '',
                        jobGradePriority: res.data.jobGradePriority || 0,
                        isActive: res.data.isActive !== undefined ? res.data.isActive : true,
                    });
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchJobGrade();
    }, [jobGradeId]);
    
    const checkoutSchema = yup.object().shape({
        jobGradeCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = jobGradeId ? 'PUT' : 'POST';
            const url = jobGradeId 
                ? `/jobgrades/update/${jobGradeId}` 
                : '/jobgrades/new';

            await api({
                method,
                url,
                data: {
                    jobGradeCode: values.jobGradeCode,
                    description: values.description,
                    jobGradeType: values.jobGradeType,
                    jobGradePriority: values.jobGradePriority,
                    isActive: values.isActive,
                },
            });

            toast.success(jobGradeId 
                ? "Job grade updated successfully" 
                : "Job grade created successfully"
            );

            // onSubmitProps.resetForm(); 
            navigate("/jobgrades");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to submit job grade!");
            setError(error);
        }
    };

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Job Grade"
                    subtitle={`${loading ? 'Loading...' : jobGradeId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/jobgrades")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        label="Job Grade Code"
                                        name="jobGradeCode"
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.jobGradeCode}
                                        error={!!touched.jobGradeCode && !!errors.jobGradeCode}
                                        helperText={touched.jobGradeCode && errors.jobGradeCode}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <TextField
                                        label="Job Grade Type"
                                        name="jobGradeType"
                                        select
                                        variant="filled"
                                        value={values.jobGradeType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText={touched.jobGradeType && errors.jobGradeType}
                                        error={!!touched.jobGradeType && !!errors.jobGradeType}
                                        sx={{ gridColumn: 'span 6' }}
                                    >
                                        {[
                                            { value: "BOM", label: "Board of Management" },
                                            { value: "BOD", label: "Board of Directors" },
                                            { value: "Director", label: "Director" },
                                            { value: "Manager", label: "Manager" },
                                            { value: "Crew", label: "Crew" },
                                            { value: "Engineer", label: "Engineer" },
                                            { value: "Executive", label: "Executive" },
                                            { value: "Officer", label: "Officer" },
                                            { value: "Other", label: "Other" },
                                        ].map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}                                            
                                    </TextField>
                                    <TextField
                                        label="Job Grade Priority"
                                        name="jobGradePriority"
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.jobGradePriority}
                                        error={!!touched.jobGradePriority && !!errors.jobGradePriority}
                                        helperText={touched.jobGradePriority && errors.jobGradePriority}
                                        slotProps={{
                                            inputProps: { min: 0, style: { textAlign: 'right' } } 
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControlLabel
                                        label="Is Active"
                                        control={
                                            <Checkbox
                                            checked={values.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            color="primary"
                                            />
                                        }
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {jobGradeId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default JobGradePage;
